import { useEffect, useState, useContext } from 'react'
import pclnRequest from '@pcln/request'

import BootstrapDataContext from '@/context/BootstrapDataContext'

import {
  buildHotelInventoryRequest,
  buildItineraryDetailsRequest,
  buildRCInventoryRequest
} from './utils'
import {
  Booking,
  CarInventory,
  CarInventoryParams,
  HotelInventory,
  HotelItineraryDetails,
  Product
} from './types'

async function getHotelItineraryDetails(
  offerToken: string
): Promise<HotelItineraryDetails> {
  const itineraryResponse = await pclnRequest(
    buildItineraryDetailsRequest(offerToken, 'AES')
  )
  const { data: { itineraryDetails = {} } = {} } = itineraryResponse.data
  return itineraryDetails.hotelXsellCarousel ?? {}
}

type CrossSellResponseType = {
  rcXsell: Partial<CarInventory> | null
  integratedListings: HotelInventory
}

/**
 * Custom Hook to get XSell Inventory on client side, as it is done in a useEffect callback
 */
export default function useCrossSellInventory(booking: Booking) {
  const [data, setData] = useState<
    (HotelInventory | CarInventory) & { offerId: string }
  >({
    listings: [],
    offerId: booking.offerId || ''
  })
  const [loading, setIsLoading] = useState(true)
  const {
    isMobile = false,
    referral,
    webstats
  } = useContext(BootstrapDataContext)
  const cguid: string = webstats.clientGUID

  useEffect(() => {
    async function getXSell() {
      try {
        const {
          productId: productType,
          destinationCity: pickupLocation,
          travelEndDateTime: returnDateTime,
          travelStartDateTime: pickupDateTime,
          secureToken: offerToken,
          offerId
        } = booking
        let requestOptions
        if (productType !== Product.Hotel) {
          const hotelItineraryDetails = await getHotelItineraryDetails(
            offerToken
          )
          requestOptions = buildHotelInventoryRequest({
            ...hotelItineraryDetails,
            appc: isMobile ? 'MOBILE' : 'DESKTOP',
            rid: referral.id ?? '',
            productId: productType,
            pageSize: isMobile ? 2 : 3,
            dealTypes: false,
            cguid
          })
        } else {
          const carInventoryParams: CarInventoryParams = {
            pickupDateTime,
            returnDateTime,
            pickupLocation,
            returnLocation: pickupLocation,
            offerToken,
            tokenType: 'AES'
          }
          requestOptions = buildRCInventoryRequest(carInventoryParams)
        }
        const response = await pclnRequest(requestOptions)
        const {
          data: { integratedListings, rcXsell }
        }: { data: CrossSellResponseType } = response.data
        if (productType === Product.Hotel) {
          setData(
            rcXsell?.listings
              ? ({ ...rcXsell, offerId } as CarInventory & {
                  offerId: string
                })
              : { listings: [], offerId }
          )
        } else {
          setData({ ...integratedListings, offerId })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('Failed to load inventory ')
      } finally {
        setIsLoading(false)
      }
    }

    getXSell()
  }, [booking, cguid, isMobile, referral.id])

  return { ...data, loading }
}
